import { Alert, Button, Field, Modal } from '@catering/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FormEvent, type JSX } from 'react';
import { useForm } from 'react-hook-form';

import { AddressFields } from '../../../customers/components/address-fields';
import {
  DEFAULT_SITE_FORM_VALUES,
  type SiteFormValues,
  siteFormSchema,
} from '../../../customers/site-form-schema';

export interface SiteFormProps {
  title: string;
  submitLabel: string;
  defaultValues?: SiteFormValues;
  submitting: boolean;
  formError?: string;
  onCancel: () => void;
  onSubmit: (values: SiteFormValues) => Promise<void>;
}

export function SiteForm({
  title,
  submitLabel,
  defaultValues,
  submitting,
  formError,
  onCancel,
  onSubmit,
}: SiteFormProps): JSX.Element {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SiteFormValues>({
    resolver: zodResolver(siteFormSchema),
    defaultValues: defaultValues ?? DEFAULT_SITE_FORM_VALUES,
  });

  function handleFormSubmit(event: FormEvent): void {
    void handleSubmit((values) => onSubmit(values))(event);
  }

  return (
    <Modal open onClose={onCancel} title={title} className="max-w-lg">
      <form onSubmit={handleFormSubmit} className="flex flex-col gap-4" noValidate>
        {formError !== undefined ? <Alert tone="danger">{formError}</Alert> : null}

        <Field
          label="Nombre"
          required
          {...register('name')}
          {...maybeError(errors.name?.message)}
        />

        <AddressFields
          legend="Domicilio"
          register={(field) => register(`address.${field}`)}
          errors={{
            street: errors.address?.street?.message,
            number: errors.address?.number?.message,
            city: errors.address?.city?.message,
            province: errors.address?.province?.message,
            postalCode: errors.address?.postalCode?.message,
          }}
        />

        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Hora de inicio"
            type="time"
            {...register('deliveryWindowStart')}
            {...maybeError(errors.deliveryWindowStart?.message)}
          />
          <Field
            label="Hora de fin"
            type="time"
            {...register('deliveryWindowEnd')}
            {...maybeError(errors.deliveryWindowEnd?.message)}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Latitud"
            {...register('latitude')}
            {...maybeError(errors.latitude?.message)}
          />
          <Field
            label="Longitud"
            {...register('longitude')}
            {...maybeError(errors.longitude?.message)}
          />
        </div>

        <Field
          label="Instrucciones de recepción"
          {...register('receptionInstructions')}
          {...maybeError(errors.receptionInstructions?.message)}
        />
        <Field
          label="Receptor habitual"
          {...register('defaultReceiverName')}
          {...maybeError(errors.defaultReceiverName?.message)}
        />
        <Field
          label="Teléfono de contacto"
          {...register('contactPhone')}
          {...maybeError(errors.contactPhone?.message)}
        />

        <div className="mt-2 flex items-center gap-3">
          <Button type="submit" loading={submitting}>
            {submitLabel}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function maybeError(message: string | undefined): { error: string } | Record<string, never> {
  return message === undefined ? {} : { error: message };
}
